import React, { useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, LogOut, User, Settings, LayoutDashboard, Grid3X3, ListChecks, Info, Shield, Sun, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/contexts/ThemeContext';

const navLinkClass = ({ isActive }) =>
  cn(
    'text-sm font-medium transition-colors hover:text-primary px-2 py-1 rounded-md',
    isActive ? 'text-primary bg-accent' : 'text-muted-foreground'
  );

function displayName(me) {
  if (!me) return '';
  const name = [me.first_name, me.last_name].filter(Boolean).join(' ').trim();
  return name || me.email || 'Account';
}

function initials(me) {
  if (!me) return '?';
  const a = (me.first_name || '').charAt(0);
  const b = (me.last_name || '').charAt(0);
  const out = `${a}${b}`.toUpperCase();
  return out || (me.email || '?').charAt(0).toUpperCase();
}

function AdminSubNav() {
  const location = useLocation();
  const navigate = useNavigate();
  if (!location.pathname.startsWith('/admin')) return null;

  const value = location.pathname.startsWith('/admin/users')
    ? '/admin/users'
    : location.pathname.startsWith('/admin/config')
      ? '/admin/config'
      : location.pathname.startsWith('/admin/broadcast')
        ? '/admin/broadcast'
        : '/admin';

  return (
    <div className="border-b border-border bg-card/40">
      <div className="mx-auto max-w-7xl px-4 py-2">
        <Tabs value={value} onValueChange={(v) => navigate(v)}>
          <TabsList>
            <TabsTrigger value="/admin">Overview</TabsTrigger>
            <TabsTrigger value="/admin/users">Users & Entries</TabsTrigger>
            <TabsTrigger value="/admin/config">Config</TabsTrigger>
            <TabsTrigger value="/admin/broadcast">Broadcast</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}

function NavItems({ me, onNavigate }) {
  const close = () => onNavigate?.();
  return (
    <>
      {me ? (
        <NavLink to="/" end className={navLinkClass} onClick={close}>
          <span className="inline-flex items-center gap-1.5"><LayoutDashboard className="h-4 w-4" />Dashboard</span>
        </NavLink>
      ) : (
        <NavLink to="/" end className={navLinkClass} onClick={close}>
          Home
        </NavLink>
      )}
      <NavLink to="/grid" className={navLinkClass} onClick={close}>
        <span className="inline-flex items-center gap-1.5"><Grid3X3 className="h-4 w-4" />Grid</span>
      </NavLink>
      {me && (
        <NavLink to="/entries" className={navLinkClass} onClick={close}>
          <span className="inline-flex items-center gap-1.5"><ListChecks className="h-4 w-4" />Entries</span>
        </NavLink>
      )}
      <NavLink to="/info" className={navLinkClass} onClick={close}>
        <span className="inline-flex items-center gap-1.5"><Info className="h-4 w-4" />Info</span>
      </NavLink>
      {me?.is_admin && (
        <NavLink to="/admin" className={navLinkClass} onClick={close}>
          <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4" />Admin</span>
        </NavLink>
      )}
    </>
  );
}

function ThemeToggle() {
  const { resolved, cyclePreference } = useTheme();
  const isDark = resolved === 'dark';
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';
  const Icon = isDark ? Moon : Sun;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      onClick={cyclePreference}
    >
      <Icon className="h-5 w-5" />
    </Button>
  );
}

export function AppShell({ me, onLogout, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-full flex flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <SheetHeader>
                  <SheetTitle>SBCC Tears</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-2" aria-label="Mobile">
                  <NavItems me={me} onNavigate={() => setMobileOpen(false)} />
                </nav>
              </SheetContent>
            </Sheet>
          </div>

          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight text-foreground no-underline hover:text-primary">
            <span className="rounded-md bg-primary/15 px-2 py-0.5 text-primary text-xs font-bold">SBCC</span>
            <span className="hidden sm:inline">Tears</span>
          </Link>

          <nav className="ml-4 hidden items-center gap-1 md:flex" aria-label="Primary">
            <NavItems me={me} />
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {me ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary">
                      {initials(me)}
                    </span>
                    <span className="hidden max-w-[10rem] truncate sm:inline">{displayName(me)}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{displayName(me)}</p>
                      <p className="text-xs text-muted-foreground">{me.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate('/account')}>
                    <User className="h-4 w-4" /> Account
                  </DropdownMenuItem>
                  {me.is_admin && (
                    <DropdownMenuItem onSelect={() => navigate('/admin')}>
                      <Settings className="h-4 w-4" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => { onLogout?.(); navigate('/'); }}>
                    <LogOut className="h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button size="sm" onClick={() => navigate('/auth')}>Log In</Button>
            )}
          </div>
        </div>
        <AdminSubNav />
      </header>

      <Separator className="opacity-0" />
      <main className="flex-1">{children}</main>
    </div>
  );
}

export default AppShell;
